import type { SyntheticEvent } from 'react';

import { AnalyticsEvents, captureAnalyticsEvent } from '@lib/analytics/events';
import type { HomepageFaqEntry } from '@lib/homepage/faq';

const HEADING_ID = 'homepage-faq-heading';
const FAQ_PLACEMENT = 'homepage';

type HomepageFaqProps = {
  entries: HomepageFaqEntry[];
};

/**
 * Native disclosure widgets: every answer is in the HTML whether or not it is
 * open, which is what lets the FAQPage structured data match it word for word.
 */
const HomepageFaq = ({ entries }: HomepageFaqProps) => {
  const handleToggle =
    (question: string) => (event: SyntheticEvent<HTMLDetailsElement>) => {
      if (!event.currentTarget.open) return;
      captureAnalyticsEvent(AnalyticsEvents.faqOpened, {
        placement: FAQ_PLACEMENT,
        question
      });
    };

  return (
    <section aria-labelledby={HEADING_ID} className="bg-landing-surface">
      <div className="mx-auto max-w-3xl px-4 py-12 md:px-8 lg:py-16">
        <h2
          id={HEADING_ID}
          className="font-sans text-2xl font-bold leading-8 text-landing-ink lg:text-[28px] lg:leading-9"
        >
          FAQ
        </h2>
        <div className="mt-6 space-y-3">
          {entries.map(({ question, answer }) => (
            <details
              key={question}
              onToggle={handleToggle(question)}
              className="group rounded-xl border border-landing-hairline bg-white px-5 py-4"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-sans text-base font-semibold text-landing-ink [&::-webkit-details-marker]:hidden">
                <h3 className="font-sans text-base font-semibold">
                  {question}
                </h3>
                <span
                  aria-hidden="true"
                  className="shrink-0 text-xl font-normal leading-none text-landing-muted transition-transform group-open:rotate-45 motion-reduce:transition-none"
                >
                  +
                </span>
              </summary>
              <p className="mt-3 font-open-sans text-sm leading-6 text-landing-slate">
                {answer}
              </p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomepageFaq;
